import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { StatusPill } from "@ozpanel/kit";
import { Button, Panel, Section, StateView, formatBytes, useAuth } from "@ozpanel/ui";

// StorageCluster — shared storage across the cluster, from GET /juicefs/cluster.
//
// One screen for "is every node serving every volume, and how well": per volume the metadata engine and
// its master, every client session the engine holds, and each node's OWN measurement (latency, load,
// bandwidth, errors, cache, whether reads work) — plus Garage, where file content and its copies live.
// Every figure is measured by the node it describes and published as a row; a node that stopped
// measuring shows STALE, never its last good numbers as if they were current.

interface Measure {
  mounted: boolean;
  read_problem?: string;
  metrics_error?: string;
  meta_latency_ms: number | null;
  meta_ops_per_sec: number | null;
  fuse_latency_ms: number | null;
  fuse_ops_per_sec: number | null;
  read_bytes_per_sec: number | null;
  write_bytes_per_sec: number | null;
  object_errors: number | null;
  io_errors: number | null;
  tx_restarts: number | null;
  cache_hit_ratio: number | null;
  cache_bytes: number;
  used_space_bytes: number;
  used_inodes: number;
}
interface NodeRow {
  node: string;
  age_seconds: number;
  stale: boolean;
  measure: Measure;
}
interface Session {
  sid: number;
  node: string;
  mount_point: string;
  version: string;
  mount_time: string;
  expire: string;
  stale: boolean;
  expired_for?: string;
}
interface VolumeView {
  volume: string;
  engine?: string;
  metadata_master?: string;
  sessions: Session[];
  sessions_error?: string;
  nodes: NodeRow[] | null;
  nodes_without_measurement?: string[];
  nodes_without_live_session?: string[];
}
interface GarageNode {
  hostname: string;
  up: boolean;
  zone?: string;
  layout_capacity_bytes?: number;
  data_disk_available_bytes: number;
  data_disk_total_bytes: number;
  data_disk_used_pct?: number;
  self: boolean;
  blocks?: number;
  resync_queue?: number;
  resync_errors?: number;
}
interface GarageView {
  status: string;
  storage_nodes: number;
  storage_nodes_up: number;
  partitions: number;
  partitions_all_copies: number;
  nodes: GarageNode[];
  stats_error?: string;
}
interface ClusterView {
  enabled: boolean;
  registered_nodes: string[];
  volumes: VolumeView[];
  garage?: GarageView;
  garage_error?: string;
  problems: string[] | null;
  summary: string;
}

const ms = (v: number | null | undefined) => (v == null ? "—" : `${v < 10 ? v.toFixed(1) : Math.round(v)} ms`);
const rate = (v: number | null | undefined) => (v == null ? "—" : `${formatBytes(v)}/s`);
const num = (v: number | null | undefined, d = 1) => (v == null ? "—" : v.toFixed(d));

function latencyTone(v: number | null | undefined): "ok" | "warn" | "bad" | "neutral" {
  if (v == null) return "neutral";
  if (v >= 500) return "bad";
  if (v >= 150) return "warn";
  return "ok";
}

function NodeTable({ v }: { v: VolumeView }) {
  const sessionOf = (node: string) => v.sessions.find((s) => s.node === node);
  const rows = v.nodes ?? [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-faint">
            <th className="px-3 py-2">Node</th>
            <th className="px-3 py-2">Serving</th>
            <th className="px-3 py-2">Session</th>
            <th className="px-3 py-2" title="average metadata operation over the last interval">Metadata</th>
            <th className="px-3 py-2" title="average filesystem operation as applications see it">File ops</th>
            <th className="px-3 py-2">Ops/s</th>
            <th className="px-3 py-2">Read</th>
            <th className="px-3 py-2">Write</th>
            <th className="px-3 py-2" title="block-cache hit ratio over the last interval">Cache hit</th>
            <th className="px-3 py-2" title="disk this node's client uses for its local block cache">Local cache</th>
            <th className="px-3 py-2" title="real I/O errors to applications / object-store errors, last interval">Errors</th>
            <th className="px-3 py-2">Measured</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const m = r.measure;
            const s = sessionOf(r.node);
            const errs = (m.io_errors ?? 0) + (m.object_errors ?? 0);
            return (
              <tr key={r.node} className="border-b border-line last:border-0">
                <td className="px-3 py-2 font-mono">{r.node}</td>
                <td className="px-3 py-2">
                  {r.stale ? (
                    <StatusPill tone="warn">stale</StatusPill>
                  ) : !m.mounted ? (
                    <StatusPill tone="bad">NOT mounted</StatusPill>
                  ) : m.read_problem ? (
                    <span title={m.read_problem}>
                      <StatusPill tone="bad">reads hang</StatusPill>
                    </span>
                  ) : (
                    <StatusPill tone="ok">serving</StatusPill>
                  )}
                </td>
                <td className="px-3 py-2">
                  {!s ? (
                    <StatusPill tone={v.sessions_error ? "neutral" : "bad"}>{v.sessions_error ? "unknown" : "none"}</StatusPill>
                  ) : s.stale ? (
                    <span title={`expired ${s.expired_for} ago`}>
                      <StatusPill tone="bad">expired</StatusPill>
                    </span>
                  ) : (
                    <span title={`sid ${s.sid} · ${s.version} · mounted ${s.mount_time}`}>
                      <StatusPill tone="ok">live</StatusPill>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <StatusPill tone={latencyTone(m.meta_latency_ms)}>{ms(m.meta_latency_ms)}</StatusPill>
                </td>
                <td className="px-3 py-2">{ms(m.fuse_latency_ms)}</td>
                <td className="px-3 py-2 font-mono">{num(m.fuse_ops_per_sec)}</td>
                <td className="px-3 py-2 font-mono">{rate(m.read_bytes_per_sec)}</td>
                <td className="px-3 py-2 font-mono">{rate(m.write_bytes_per_sec)}</td>
                <td className="px-3 py-2 font-mono">{m.cache_hit_ratio == null ? "—" : `${Math.round(100 * m.cache_hit_ratio)}%`}</td>
                <td className="px-3 py-2 font-mono">{m.cache_bytes ? formatBytes(m.cache_bytes) : "—"}</td>
                <td className="px-3 py-2">
                  {m.io_errors == null && m.object_errors == null ? (
                    "—"
                  ) : (
                    <StatusPill tone={errs > 0 ? "bad" : "ok"}>
                      {m.io_errors ?? 0} / {m.object_errors ?? 0}
                    </StatusPill>
                  )}
                </td>
                <td className="px-3 py-2 text-faint" title={m.metrics_error}>
                  {r.age_seconds}s ago{m.metrics_error ? " · metrics unreadable" : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(v.nodes_without_measurement ?? []).length > 0 && (
        <p className="px-3 py-2 text-[12px] text-warn">
          Never measured this volume: {(v.nodes_without_measurement ?? []).join(", ")}
        </p>
      )}
      {v.sessions_error && <p className="px-3 py-2 text-[12px] text-warn">{v.sessions_error}</p>}
    </div>
  );
}

function GaragePanel({ g, err }: { g?: GarageView; err?: string }) {
  if (err) return <p className="text-[12.5px] text-crit">Garage could not be asked: {err}</p>;
  if (!g) return null;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <StatusPill tone={g.status === "healthy" ? "ok" : "bad"}>{g.status}</StatusPill>
        <span>
          {g.storage_nodes_up}/{g.storage_nodes} storage nodes up
        </span>
        <span className="text-faint">·</span>
        <span>
          {g.partitions_all_copies}/{g.partitions} partitions with every copy
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {g.nodes.map((n) => {
          const pct = n.data_disk_used_pct ?? 0;
          const tone = pct >= 85 ? "bg-crit" : pct >= 70 ? "bg-warn" : "bg-ok";
          return (
            <Panel key={n.hostname} className="p-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[12.5px]">{n.hostname.split(".")[0]}</span>
                <StatusPill tone={n.up ? "ok" : "bad"}>{n.up ? "up" : "DOWN"}</StatusPill>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded bg-surface2">
                <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <div className="mt-1 text-[11.5px] text-muted">
                data disk {pct.toFixed(0)}% used · {formatBytes(n.data_disk_available_bytes)} free of {formatBytes(n.data_disk_total_bytes)}
              </div>
              <div className="mt-1 text-[11.5px] text-muted">
                {n.blocks == null ? (
                  "block statistics not reported"
                ) : (
                  <>
                    {n.blocks} blocks · resync queue{" "}
                    <span className={(n.resync_queue ?? 0) > 0 ? "text-warn" : ""}>{n.resync_queue}</span> · errors{" "}
                    <span className={(n.resync_errors ?? 0) > 0 ? "text-crit" : ""}>{n.resync_errors}</span>
                  </>
                )}
              </div>
            </Panel>
          );
        })}
      </div>
      {g.stats_error && <p className="text-[12px] text-warn">{g.stats_error}</p>}
    </div>
  );
}

// ---- Garage data directories: add a disk, drain one, remove it — per node, measured by that node.

interface DirState {
  ready: boolean;
  problem?: string;
  fs_type?: string;
  shares_root_filesystem: boolean;
  fs_total_bytes: number;
  fs_available_bytes: number;
  blocks?: number;
  rendered: boolean;
  read_only: boolean;
  rebalance_launched_at?: string;
  rebalance_note?: string;
  note?: string;
}
interface DirRow {
  id: number;
  node: string;
  path: string;
  capacity_bytes: number;
  mode: "active" | "draining" | "removing";
  state?: DirState;
  measured_at?: string;
}
interface NodeDirs {
  node: string;
  explicit: boolean;
  dirs: DirRow[];
}

function ago(ts?: string) {
  if (!ts) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
  return s < 90 ? `${s}s ago` : `${Math.round(s / 60)} min ago`;
}

function DataDirs() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["garage-data-dirs"],
    queryFn: () => client.apiFetch<{ nodes: NodeDirs[]; note: string }>("/garage/data-dirs"),
    refetchInterval: 15000,
    retry: false,
  });
  const [form, setForm] = React.useState({ node: "", path: "", capacity_gb: "" });
  const done = () => qc.invalidateQueries({ queryKey: ["garage-data-dirs"] });
  const add = useMutation({
    mutationFn: () =>
      client.apiFetch<unknown>("/garage/data-dirs", {
        method: "POST",
        body: JSON.stringify({ node: form.node, path: form.path.trim(), capacity_gb: Number(form.capacity_gb || 0) }),
      }),
    onSuccess: () => {
      setForm({ node: "", path: "", capacity_gb: "" });
      done();
    },
  });
  const mode = useMutation({
    mutationFn: (a: { id: number; mode: string }) =>
      client.apiFetch<unknown>(`/garage/data-dirs/${a.id}/mode`, { method: "POST", body: JSON.stringify({ mode: a.mode }) }),
    onSettled: done,
  });
  const remove = useMutation({
    mutationFn: (id: number) => client.apiFetch<unknown>(`/garage/data-dirs/${id}`, { method: "DELETE" }),
    onSettled: done,
  });
  const nodes = q.data?.nodes ?? [];
  const err = (add.error ?? mode.error ?? remove.error) as any;
  return (
    <StateView loading={q.isLoading} error={q.error} errorContext="the Garage data directories">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <select
            className="rounded border border-line bg-surface px-2 py-1 text-[13px]"
            value={form.node}
            onChange={(e) => setForm({ ...form, node: e.target.value })}
          >
            <option value="">node…</option>
            {nodes.map((n) => (
              <option key={n.node} value={n.node}>
                {n.node}
              </option>
            ))}
          </select>
          <input
            className="w-72 rounded border border-line bg-surface px-2 py-1 font-mono text-[13px]"
            placeholder="/srv/disk2/garage (already mounted)"
            value={form.path}
            onChange={(e) => setForm({ ...form, path: e.target.value })}
          />
          <input
            className="w-36 rounded border border-line bg-surface px-2 py-1 text-[13px]"
            placeholder="capacity GB (0 = disk)"
            value={form.capacity_gb}
            onChange={(e) => setForm({ ...form, capacity_gb: e.target.value.replace(/[^0-9]/g, "") })}
          />
          <Button size="sm" variant="accent" disabled={!form.node || !form.path || add.isPending} onClick={() => add.mutate()}>
            Add data directory
          </Button>
        </div>
        {err && <p className="text-[12.5px] text-crit">{String(err?.message ?? err)}</p>}
        <p className="text-[12px] text-muted">
          To replace a disk: mount the new one, add it here, wait for its node to measure it, then drain the old one — its
          blocks move while it stays readable — and remove it once its node counts it empty. {q.data?.note}
        </p>
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-faint">
              <th className="px-3 py-2">Node</th>
              <th className="px-3 py-2">Directory</th>
              <th className="px-3 py-2">Mode</th>
              <th className="px-3 py-2">Measured by the node</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {nodes.flatMap((n) =>
              n.dirs.map((d) => {
                const s = d.state;
                return (
                  <tr key={`${n.node}-${d.id}-${d.path}`} className="border-b border-line last:border-0 align-top">
                    <td className="px-3 py-2 font-mono">{n.node}</td>
                    <td className="px-3 py-2 font-mono">
                      {d.path}
                      {!n.explicit && <span className="ml-2 text-faint">(built-in)</span>}
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill tone={d.mode === "active" ? "ok" : "warn"}>{d.mode}</StatusPill>
                    </td>
                    <td className="px-3 py-2 text-muted">
                      {!n.explicit ? (
                        "the node's only directory"
                      ) : !s ? (
                        <span className="text-warn">not measured yet</span>
                      ) : !s.ready ? (
                        <span className="text-crit">{s.problem}</span>
                      ) : (
                        <>
                          {formatBytes(s.fs_available_bytes)} free of {formatBytes(s.fs_total_bytes)}
                          {s.shares_root_filesystem && <span className="text-warn"> · shares the root filesystem</span>}
                          {s.blocks != null && <> · <strong>{s.blocks}</strong> blocks left</>}
                          {s.rendered ? (s.read_only ? " · read-only in Garage" : " · in use") : " · not in Garage's config"}
                          {s.rebalance_note && <span className="text-crit"> · {s.rebalance_note}</span>}
                          {s.note && <> · {s.note}</>}
                          <span className="text-faint"> · {ago(d.measured_at)}</span>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {n.explicit && s && !s.ready && !s.rendered ? (
                        <Button size="sm" variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(d.id)}>
                          Remove (never used)
                        </Button>
                      ) : (
                        n.explicit &&
                        d.mode === "active" && (
                          <Button size="sm" variant="default" disabled={mode.isPending} onClick={() => mode.mutate({ id: d.id, mode: "draining" })}>
                            Drain
                          </Button>
                        )
                      )}
                      {n.explicit && d.mode === "draining" && (
                        <>
                          <Button size="sm" variant="ghost" disabled={mode.isPending} onClick={() => mode.mutate({ id: d.id, mode: "active" })}>
                            Cancel drain
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={remove.isPending || s?.blocks !== 0}
                            title={s?.blocks === 0 ? "" : "enabled once the node counts it empty"}
                            onClick={() => remove.mutate(d.id)}
                          >
                            Remove
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>
    </StateView>
  );
}

export function StorageClusterView() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["juicefs-cluster"],
    queryFn: () => client.apiFetch<ClusterView>("/juicefs/cluster"),
    refetchInterval: 30000,
    retry: false,
  });
  const d = q.data;
  const problems = d?.problems ?? [];
  return (
    <StateView loading={q.isLoading} error={q.error} errorContext="the storage cluster view">
      <div className="space-y-6">
        <Panel className={`p-4 ${problems.length ? "border-[var(--ozp-crit)]" : ""}`}>
          <div className="flex items-start gap-3">
            <StatusPill tone={problems.length ? "bad" : "ok"}>{problems.length ? `${problems.length} problem(s)` : "all serving"}</StatusPill>
            <span className="text-[13px]">{d?.summary}</span>
          </div>
        </Panel>
        {(d?.volumes ?? []).map((v) => (
          <Section
            key={v.volume}
            title={`Volume · ${v.volume}`}
            actions={
              <span className="text-[12px] text-muted">
                {(() => {
                  const fresh = (v.nodes ?? []).find((r) => !r.stale && r.measure.used_space_bytes > 0);
                  return fresh ? `${formatBytes(fresh.measure.used_space_bytes)} used · ${fresh.measure.used_inodes.toLocaleString()} files · ` : "";
                })()}
                metadata on {v.engine ?? "?"}
                {v.metadata_master ? ` · master ${v.metadata_master}` : ""}
              </span>
            }
            bodyClassName="p-0"
          >
            <NodeTable v={v} />
          </Section>
        ))}
        <Section title="Garage · where file content and its copies live">
          <GaragePanel g={d?.garage} err={d?.garage_error} />
        </Section>
        <Section title="Garage · data directories (disks)">
          <DataDirs />
        </Section>
      </div>
    </StateView>
  );
}
